/**
 * Checkout Funnel Tracking Hook — Extended
 * Tracks anonymous funnel events to both:
 * 1. Own DB table (consent-independent, GDPR-compliant – no PII)
 * 2. GA4 Enhanced E-Commerce events (consent-dependent)
 */
import { useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getCookiePreferences } from '@/components/CookieConsent';
import { parseUA } from '@/lib/uaParser';

// Funnel steps in order
export type FunnelEvent =
  | 'page_view'            // Every page visit
  | 'product_viewed'
  | 'product_clicked'      // User clicked product from list (GA4: select_item)
  | 'card_customized'      // User started customizing
  | 'field_interaction'    // User interacted with a form field
  | 'card_back_viewed'     // User flipped to back side
  | 'card_back_skipped'    // User added to cart without checking back
  | 'photo_upload_attempt' // Photo upload started
  | 'photo_upload_success' // Photo upload completed
  | 'photo_upload_fail'    // Photo upload failed
  | 'scroll_depth'         // Scroll milestone reached
  | 'exit_intent'          // User showed exit intent
  | 'exit_intent_cta'      // User clicked CTA in exit popup
  | 'exit_intent_dismiss'  // User dismissed exit popup
  | 'cart_added'
  | 'cart_removed'         // Item removed from cart
  | 'cart_viewed'
  | 'checkout_started'
  | 'checkout_field_focus' // User focused a checkout field
  | 'shipping_info_added'  // Shipping address submitted (GA4: add_shipping_info)
  | 'payment_initiated'    // Redirect to Stripe
  | 'payment_redirect_timing' // Time from click to redirect
  | 'payment_completed'
  | 'promo_viewed'         // Promo banner shown (GA4: view_promotion)
  | 'promo_clicked'        // Promo banner clicked (GA4: select_promotion)
  | 'user_signed_up'       // User registration (GA4: sign_up)
  | 'user_logged_in'       // User login (GA4: login)
  | 'share';               // Social sharing (GA4: share)

export interface EventData {
  product_slug?: string;
  product_name?: string;
  cart_value?: number;
  items_count?: number;
  currency?: string;
  // Extended fields
  field_key?: string;
  field_time_ms?: number;
  scroll_percent?: number;
  page_path?: string;
  file_size_kb?: number;
  file_format?: string;
  error_message?: string;
  redirect_time_ms?: number;
  time_in_cart_ms?: number;
  transaction_id?: string;
  item_category?: string;
  tax_amount?: number;
  coupon?: string;
  ga4_items?: unknown[];
  // Promotion fields
  promotion_id?: string;
  promotion_name?: string;
  creative_name?: string;
  creative_slot?: string;
  // Auth fields
  method?: string;
  // List fields
  item_list_id?: string;
  item_list_name?: string;
  index?: number;
  // Share fields
  content_type?: string;
  // Shipping
  shipping_tier?: string;
  [key: string]: unknown;
}

// ----- Persistent Visitor ID (cross-session) -----
export function getVisitorId(): string {
  const KEY = 'funnel_visitor_id';
  let id = localStorage.getItem(KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(KEY, id);
  }
  return id;
}

// ----- Session ID (per tab session) -----
export function getSessionId(): string {
  const KEY = 'funnel_session_id';
  let id = sessionStorage.getItem(KEY);
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem(KEY, id);
  }
  return id;
}

// ----- Admin detection (multi-signal) -----
const ADMIN_FLAG_KEY = 'funnel_is_admin';
const ADMIN_UA_KEY = 'funnel_admin_ua';
const ADMIN_FINGERPRINTS_KEY = 'funnel_admin_fingerprints';

/** Generate a lightweight browser fingerprint (screen + UA + timezone) */
function getBrowserFingerprint(): string {
  const parts = [
    navigator.userAgent,
    `${screen.width}x${screen.height}x${screen.colorDepth}`,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    navigator.language,
    navigator.hardwareConcurrency?.toString() || '',
  ];
  // Simple hash
  let hash = 0;
  const str = parts.join('|');
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash |= 0;
  }
  return hash.toString(36);
}

/** Mark current visitor as admin (called from admin login flow) */
export function markVisitorAsAdmin() {
  localStorage.setItem(ADMIN_FLAG_KEY, 'true');
}

/** Clear admin flag (called on sign-out) */
export function clearAdminFlag() {
  localStorage.removeItem(ADMIN_FLAG_KEY);
  localStorage.removeItem(ADMIN_UA_KEY);
  localStorage.removeItem(ADMIN_FINGERPRINTS_KEY);
}

/** Detect if current session belongs to an admin/employee */
function isAdminSession(): boolean {
  // Signal 1: Explicit admin flag set during admin login
  if (localStorage.getItem(ADMIN_FLAG_KEY) === 'true') return true;
  // Signal 2: Currently on an admin page
  if (window.location.pathname.startsWith('/admin')) return true;
  return false;
}

/** Get admin detection metadata to store with events for server-side filtering */
function getAdminDetectionMeta(): Record<string, unknown> {
  if (!isAdminSession()) return {};
  return {
    is_admin: true,
    admin_ua_hash: getBrowserFingerprint(),
    admin_signals: [
      localStorage.getItem(ADMIN_FLAG_KEY) === 'true' ? 'flag' : null,
      window.location.pathname.startsWith('/admin') ? 'path' : null,
      (() => { try { const fps = JSON.parse(localStorage.getItem(ADMIN_FINGERPRINTS_KEY) || '[]'); return fps.includes(getBrowserFingerprint()) ? 'fingerprint' : null; } catch { return null; } })(),
      localStorage.getItem(ADMIN_UA_KEY) === navigator.userAgent ? 'ua_match' : null,
    ].filter(Boolean),
  };
}

// ----- Bot / Crawler detection (enhanced) -----
const BOT_UA_PATTERNS = [
  /googlebot/i, /bingbot/i, /slurp/i, /duckduckbot/i, /baiduspider/i,
  /yandexbot/i, /sogou/i, /facebookexternalhit/i, /twitterbot/i,
  /linkedinbot/i, /whatsapp/i, /telegrambot/i, /discordbot/i,
  /semrushbot/i, /ahrefsbot/i, /mj12bot/i, /dotbot/i, /petalbot/i,
  /bytespider/i, /gptbot/i, /chatgpt/i, /claudebot/i, /anthropic/i,
  /applebot/i, /ia_archiver/i, /archive\.org_bot/i,
  /rogerbot/i, /screaming frog/i, /uptimerobot/i, /pingdom/i,
  /headlesschrome/i, /phantomjs/i, /prerender/i, /lighthouse/i,
  /chrome-lighthouse/i, /pagespeed/i, /google-inspectiontool/i,
  /mediapartners-google/i, /adsbot-google/i, /apis-google/i,
  /feedfetcher/i, /bot\b/i, /crawler/i, /spider/i, /scraper/i,
  /python-requests/i, /python-urllib/i, /java\//i, /wget/i, /curl\//i,
  /go-http-client/i, /node-fetch/i, /axios/i, /libwww-perl/i,
  /httpx/i, /scrapy/i, /colly/i, /dataprovider/i, /seznambot/i,
];

/** Detect outdated Chrome versions (< 70) commonly used by bots */
function isOutdatedChrome(): boolean {
  const ua = navigator.userAgent;
  const match = ua.match(/Chrome\/(\d+)/);
  if (!match) return false;
  const majorVersion = parseInt(match[1], 10);
  // Chrome < 70 is from 2018 — virtually no real users still on these
  return majorVersion > 0 && majorVersion < 70;
}

let _isBotCached: boolean | null = null;

function isBot(): boolean {
  if (_isBotCached !== null) return _isBotCached;

  const ua = navigator.userAgent;

  // 1. Empty or suspiciously short UA
  if (!ua || ua.length < 20) { _isBotCached = true; return true; }

  // 2. Known bot patterns
  if (BOT_UA_PATTERNS.some(pattern => pattern.test(ua))) { _isBotCached = true; return true; }

  // 3. WebDriver flag (headless automation)
  if ((navigator as any).webdriver === true) { _isBotCached = true; return true; }

  // 4. Outdated Chrome versions (bots often use old Chromium)
  if (isOutdatedChrome()) { _isBotCached = true; return true; }

  // 5. Missing essential browser APIs that all real browsers have
  if (typeof window.requestAnimationFrame !== 'function' || typeof window.fetch !== 'function') {
    _isBotCached = true; return true;
  }

  _isBotCached = false;
  return false;
}

function getDeviceType(): string {
  const w = window.innerWidth;
  if (w < 768) return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
}

function getReferrerSource(): string | null {
  try {
    const ref = document.referrer;
    if (!ref) return null;
    const url = new URL(ref);
    if (url.hostname === window.location.hostname) return null;
    return url.hostname;
  } catch {
    return null;
  }
}

/** Extract UTM params from current URL (captured once per session) */
function getUtmParams(): { utm_source?: string; utm_medium?: string; utm_campaign?: string } {
  const KEY = 'funnel_utm';
  const cached = sessionStorage.getItem(KEY);
  if (cached) {
    try { return JSON.parse(cached); } catch {}
  }

  const params = new URLSearchParams(window.location.search);
  const utm: Record<string, string> = {};
  for (const key of ['utm_source', 'utm_medium', 'utm_campaign']) {
    const val = params.get(key);
    if (val) utm[key] = val;
  }

  if (Object.keys(utm).length > 0) {
    sessionStorage.setItem(KEY, JSON.stringify(utm));
  }
  return utm;
}

// Wait for gtag to become available (max ~5s, for post-redirect scenarios)
function waitForGtag(maxMs = 5000): Promise<Function | null> {
  return new Promise((resolve) => {
    const g = (window as any).gtag;
    if (typeof g === 'function') { resolve(g); return; }
    const start = Date.now();
    const iv = setInterval(() => {
      const g2 = (window as any).gtag;
      if (typeof g2 === 'function') { clearInterval(iv); resolve(g2); return; }
      if (Date.now() - start > maxMs) { clearInterval(iv); resolve(null); }
    }, 200);
  });
}

// GA4 Enhanced E-Commerce event mapping
async function sendGA4Event(event: FunnelEvent, data: EventData) {
  const prefs = getCookiePreferences();
  if (!prefs?.analytics) return;

  // For critical conversion events, wait for gtag to load (post-redirect)
  const criticalEvents: FunnelEvent[] = ['payment_completed', 'checkout_started'];
  let gtag: Function | null;
  if (criticalEvents.includes(event)) {
    gtag = await waitForGtag();
  } else {
    gtag = typeof (window as any).gtag === 'function' ? (window as any).gtag : null;
  }
  if (!gtag) return;

  const ga4Map: Partial<Record<FunnelEvent, string>> = {
    product_viewed: 'view_item',
    product_clicked: 'select_item',
    cart_added: 'add_to_cart',
    cart_removed: 'remove_from_cart',
    cart_viewed: 'view_cart',
    checkout_started: 'begin_checkout',
    shipping_info_added: 'add_shipping_info',
    payment_initiated: 'add_payment_info',
    payment_completed: 'purchase',
    promo_viewed: 'view_promotion',
    promo_clicked: 'select_promotion',
    user_signed_up: 'sign_up',
    user_logged_in: 'login',
    share: 'share',
  };

  const ga4Event = ga4Map[event];
  if (!ga4Event) return;

  // Build GA4 items array — use provided items or construct from single product
  let items: Record<string, unknown>[] | undefined;
  if (data.ga4_items && Array.isArray(data.ga4_items)) {
    items = data.ga4_items as Record<string, unknown>[];
  } else if (data.product_slug) {
    items = [{
      item_id: data.product_slug,
      item_name: data.product_name || data.product_slug,
      item_brand: 'Fake-ID.com',
      item_category: data.item_category || 'ID Cards',
      price: data.cart_value,
      quantity: data.items_count || 1,
      currency: data.currency || 'EUR',
      ...(data.item_list_id ? { item_list_id: data.item_list_id } : {}),
      ...(data.item_list_name ? { item_list_name: data.item_list_name } : {}),
      ...(typeof data.index === 'number' ? { index: data.index } : {}),
    }];
  }

  // Promotion events have a different schema
  if (ga4Event === 'view_promotion' || ga4Event === 'select_promotion') {
    gtag('event', ga4Event, {
      promotion_id: data.promotion_id || 'homepage_slider',
      promotion_name: data.promotion_name,
      creative_name: data.creative_name,
      creative_slot: data.creative_slot,
      items,
    });
    return;
  }

  // Auth events (sign_up, login)
  if (ga4Event === 'sign_up' || ga4Event === 'login') {
    gtag('event', ga4Event, { method: data.method || 'email' });
    return;
  }

  // Share event
  if (ga4Event === 'share') {
    gtag('event', ga4Event, {
      method: data.method,
      content_type: data.content_type || 'product',
      item_id: data.product_slug,
    });
    return;
  }

  const eventParams: Record<string, unknown> = {
    currency: data.currency || 'EUR',
    value: data.cart_value || 0,
    items,
  };

  // Add transaction_id for purchase events
  if (ga4Event === 'purchase' && data.transaction_id) {
    eventParams.transaction_id = data.transaction_id;
    eventParams.affiliation = 'Fake-ID.com';
    eventParams.shipping = 0;
    eventParams.tax = data.tax_amount || 0;
  }

  // Add shipping_tier for add_shipping_info
  if (ga4Event === 'add_shipping_info') {
    eventParams.shipping_tier = data.shipping_tier || 'Free Shipping';
  }

  // Add coupon if present
  if (data.coupon) {
    eventParams.coupon = data.coupon;
  }

  // Add item_list context for select_item
  if (ga4Event === 'select_item') {
    eventParams.item_list_id = data.item_list_id || 'shop';
    eventParams.item_list_name = data.item_list_name || 'Shop';
  }

  gtag('event', ga4Event, eventParams);
}

// ─── GA4 User Properties (set once per session) ───
let userPropsSet = false;
export function setGA4UserProperties() {
  if (userPropsSet) return;
  const gtag = (window as any).gtag;
  if (typeof gtag !== 'function') return;
  
  const visitorId = getVisitorId();
  const isReturning = localStorage.getItem('funnel_has_visited') === 'true';
  localStorage.setItem('funnel_has_visited', 'true');
  
  gtag('set', 'user_properties', {
    user_type: isReturning ? 'returning' : 'new',
    preferred_language: navigator.language?.split('-')[0] || 'en',
    device_category: getDeviceType(),
    visitor_id: visitorId,
  });
  
  userPropsSet = true;
}

// Deduplicate: don't fire the same event twice per session for the same product
const firedEvents = new Set<string>();

// Events that should NOT be deduplicated (can fire multiple times)
const REPEATABLE_EVENTS: Set<FunnelEvent> = new Set([
  'field_interaction', 'scroll_depth', 'page_view', 'cart_removed',
  'checkout_field_focus', 'photo_upload_attempt', 'photo_upload_success',
  'photo_upload_fail', 'payment_redirect_timing', 'exit_intent',
]);

export function useFunnelTracking() {
  const pendingRef = useRef(false);

  const trackEvent = useCallback(async (event: FunnelEvent, data: EventData = {}) => {
    // Skip all bot/crawler traffic to avoid inflated metrics
    if (isBot()) return;

    // Dedup key: event + product (or just event for non-product events)
    if (!REPEATABLE_EVENTS.has(event)) {
      const dedupeKey = `${event}:${data.product_slug || 'global'}`;
      if (firedEvents.has(dedupeKey)) return;
      firedEvents.add(dedupeKey);
    }

    // 1. Send to GA4 (consent-dependent)
    try { sendGA4Event(event, data); } catch {}

    // 2. Send to own DB (fire-and-forget, consent-independent)
    if (pendingRef.current) return;
    pendingRef.current = true;
    
    const utm = getUtmParams();
    const ua = parseUA(navigator.userAgent);

    try {
      await supabase.from('checkout_funnel_events' as any).insert([{
        session_id: getSessionId(),
        event_type: event,
        event_data: { ...data, visitor_id: getVisitorId(), ...getAdminDetectionMeta() } as any,
        device_type: getDeviceType(),
        referrer_source: getReferrerSource(),
        browser_name: ua.browserName,
        browser_version: ua.browserVersion,
        os_name: ua.osName,
        os_version: ua.osVersion,
        device_model: ua.deviceModel || null,
        user_agent_raw: navigator.userAgent,
        ...(utm.utm_source ? { utm_source: utm.utm_source } : {}),
        ...(utm.utm_medium ? { utm_medium: utm.utm_medium } : {}),
        ...(utm.utm_campaign ? { utm_campaign: utm.utm_campaign } : {}),
      }]);
    } catch {
      // Silent fail – tracking should never break the shop
    } finally {
      pendingRef.current = false;
    }
  }, []);

  return { trackEvent };
}

/** Standalone function for use outside React components */
export function trackFunnelEvent(event: FunnelEvent, data: EventData = {}) {
  // Skip all bot/crawler traffic
  if (isBot()) return;

  if (!REPEATABLE_EVENTS.has(event)) {
    const dedupeKey = `${event}:${data.product_slug || 'global'}`;
    if (firedEvents.has(dedupeKey)) return;
    firedEvents.add(dedupeKey);
  }

  try { sendGA4Event(event, data); } catch {}

  const utm = getUtmParams();
  const ua = parseUA(navigator.userAgent);

  supabase.from('checkout_funnel_events' as any).insert([{
    session_id: getSessionId(),
    event_type: event,
    event_data: { ...data, visitor_id: getVisitorId(), ...getAdminDetectionMeta() } as any,
    device_type: getDeviceType(),
    referrer_source: getReferrerSource(),
    browser_name: ua.browserName,
    browser_version: ua.browserVersion,
    os_name: ua.osName,
    os_version: ua.osVersion,
    device_model: ua.deviceModel || null,
    user_agent_raw: navigator.userAgent,
    ...(utm.utm_source ? { utm_source: utm.utm_source } : {}),
    ...(utm.utm_medium ? { utm_medium: utm.utm_medium } : {}),
    ...(utm.utm_campaign ? { utm_campaign: utm.utm_campaign } : {}),
  }]).then(() => {});
}

// ─── Utility hooks for specific tracking scenarios ───

/** Track scroll depth milestones (25%, 50%, 75%, 100%) */
export function useScrollTracking(productSlug?: string, productName?: string) {
  const milestones = useRef(new Set<number>());
  
  const handleScroll = useCallback(() => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (docHeight <= 0) return;
    const percent = Math.round((scrollTop / docHeight) * 100);
    
    for (const milestone of [25, 50, 75, 100]) {
      if (percent >= milestone && !milestones.current.has(milestone)) {
        milestones.current.add(milestone);
        trackFunnelEvent('scroll_depth', {
          scroll_percent: milestone,
          product_slug: productSlug,
          product_name: productName,
          page_path: window.location.pathname,
        });
      }
    }
  }, [productSlug, productName]);

  return { handleScroll, resetMilestones: () => milestones.current.clear() };
}

/** Track exit intent (mouse leaves viewport on desktop) */
export function useExitIntentTracking(productSlug?: string) {
  const fired = useRef(false);
  
  const handleMouseLeave = useCallback((e: MouseEvent) => {
    if (e.clientY <= 0 && !fired.current) {
      fired.current = true;
      trackFunnelEvent('exit_intent', {
        product_slug: productSlug,
        page_path: window.location.pathname,
        scroll_percent: Math.round((window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100),
      });
    }
  }, [productSlug]);

  return { handleMouseLeave, resetExitIntent: () => { fired.current = false; } };
}

/** Track time spent on a form field */
export function trackFieldInteraction(fieldKey: string, timeMs: number, productSlug?: string) {
  trackFunnelEvent('field_interaction', {
    field_key: fieldKey,
    field_time_ms: timeMs,
    product_slug: productSlug,
  });
}

/** Track photo upload results */
export function trackPhotoUpload(status: 'attempt' | 'success' | 'fail', details: {
  file_size_kb?: number;
  file_format?: string;
  error_message?: string;
  product_slug?: string;
}) {
  const event: FunnelEvent = status === 'attempt' ? 'photo_upload_attempt'
    : status === 'success' ? 'photo_upload_success' : 'photo_upload_fail';
  trackFunnelEvent(event, details);
}

/** Track page views with enhanced GA4 data */
export function trackPageView(pagePath: string) {
  trackFunnelEvent('page_view', { page_path: pagePath });
  
  // Send enhanced page_view to GA4 with content grouping
  const gtag = (window as any).gtag;
  if (typeof gtag !== 'function') return;
  
  let contentGroup = 'Other';
  if (pagePath === '/' || pagePath.match(/^\/(de|es|fr|pt|ru|it|sv|zh)\/?$/)) contentGroup = 'Homepage';
  else if (pagePath.includes('/shop') && pagePath.includes('id-card-generator')) contentGroup = 'Product';
  else if (pagePath.includes('/shop')) contentGroup = 'Shop';
  else if (pagePath.includes('/checkout')) contentGroup = 'Checkout';
  else if (pagePath.includes('/blog')) contentGroup = 'Blog';
  else if (pagePath.includes('/about')) contentGroup = 'About';
  else if (pagePath.includes('/contact')) contentGroup = 'Contact';
  else if (pagePath.includes('/reviews')) contentGroup = 'Reviews';
  
  gtag('event', 'page_view', {
    page_path: pagePath,
    page_location: window.location.href,
    page_title: document.title,
    content_group: contentGroup,
  });
}
